import { useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight } from 'lucide-react';
import { useMobile } from './hooks/useMobile';
import { useReducedMotion } from './hooks/useReducedMotion';
import { useScrollStore } from '@/stores/scrollStore';
import { MagneticButton } from './effects/MagneticButton';

const NeuralBrainScene = lazy(() => import('./3d/NeuralBrainScene'));

gsap.registerPlugin(ScrollTrigger);

export function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const isMobile = useMobile();
  const prefersReduced = useReducedMotion();
  const navigate = useNavigate();

  // Cursor-reactive glow
  useEffect(() => {
    if (isMobile) return;
    const section = sectionRef.current;
    const glow = glowRef.current;
    if (!section || !glow) return;

    const onMouseMove = (e: MouseEvent) => {
      const rect = section.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      glow.style.background = `radial-gradient(600px circle at ${x}px ${y}px, rgba(124,58,237,0.08), transparent 60%)`;
    };
    section.addEventListener('mousemove', onMouseMove);
    return () => section.removeEventListener('mousemove', onMouseMove);
  }, [isMobile]);

  // GSAP entrance + scroll animations
  useEffect(() => {
    if (prefersReduced || !sectionRef.current || !contentRef.current) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.3 });

      tl.from('.hero-eyebrow', {
        opacity: 0,
        y: 20,
        duration: 0.6,
        ease: 'power3.out',
      })
        .from('.hero-headline-line', {
          opacity: 0,
          y: 40,
          duration: 0.8,
          stagger: 0.12,
          ease: 'power3.out',
        }, '-=0.3')
        .from('.hero-subtitle', {
          opacity: 0,
          y: 30,
          duration: 0.8,
          ease: 'power3.out',
        }, '-=0.4')
        .from('.hero-cta', {
          opacity: 0,
          y: 20,
          duration: 0.6,
          stagger: 0.15,
          ease: 'power3.out',
        }, '-=0.4')
        .from('.hero-trust', {
          opacity: 0,
          duration: 0.6,
          ease: 'power2.out',
        }, '-=0.2');

      // Parallax content on scroll (desktop only)
      if (!isMobile) {
        gsap.to(contentRef.current, {
          y: -80,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: 1,
          },
        });
      }

      // Update scroll store for 3D scenes
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top top',
        end: 'bottom top',
        onUpdate: (self) => {
          useScrollStore.getState().setHeroProgress(self.progress);
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile, prefersReduced]);

  const scrollToHowItWorks = () => {
    const el = document.getElementById('how-it-works');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #06060F 0%, #1a0a2e 40%, #0a1628 70%, #06060F 100%)',
      }}
    >
      {/* 3D scene */}
      {!isMobile && (
        <Suspense fallback={null}>
          <NeuralBrainScene />
        </Suspense>
      )}

      {/* Dot grid pattern */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(232,230,240,0.03) 1px, transparent 1px)',
          backgroundSize: '30px 30px',
        }}
      />

      {/* Cursor-reactive glow */}
      <div
        ref={glowRef}
        className="absolute inset-0 z-[1] pointer-events-none transition-[background] duration-100"
      />

      {/* Purple radial ambient */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-0 pointer-events-none"
        style={{
          width: '60vw',
          height: '60vw',
          maxWidth: '900px',
          maxHeight: '900px',
          background: 'radial-gradient(ellipse, rgba(124,58,237,0.12) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      {/* Content */}
      <div
        ref={contentRef}
        className="relative z-10 mx-auto max-w-4xl px-6 text-center"
        style={{ paddingTop: 'clamp(7rem, 15vh, 12rem)', paddingBottom: 'clamp(4rem, 10vh, 8rem)' }}
      >
        {/* Eyebrow */}
        <p
          className="hero-eyebrow mb-6"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 'clamp(0.875rem, 1vw, 1.1rem)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            color: '#34D399',
          }}
        >
          Turbocharge your communication with your audience
        </p>

        {/* Headline */}
        <h1
          style={{
            fontFamily: "'Space Grotesk', system-ui, sans-serif",
            fontSize: 'clamp(3rem, 6vw + 1rem, 7.5rem)',
            fontWeight: 700,
            lineHeight: 1.0,
            letterSpacing: '-0.02em',
          }}
        >
          <span className="hero-headline-line block text-[#E8E6F0]">Automated marketing that</span>
          <span
            className="hero-headline-line block"
            style={{
              background: 'linear-gradient(135deg, #E8E6F0 30%, #7C3AED 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            wins customers.
          </span>
        </h1>

        {/* Subtitle */}
        <p
          className="hero-subtitle mx-auto mt-8 max-w-[55ch] text-[#C8C6D2]"
          style={{
            fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            fontSize: 'clamp(1.25rem, 1vw + 0.9rem, 1.5rem)',
            lineHeight: 1.65,
          }}
        >
          Posts, blogs, and case studies — all generated with 15+ behavioral-psychology
          principles, all built to convert.
        </p>

        {/* CTAs */}
        <div className="mt-10 flex flex-col items-center justify-center gap-5 sm:flex-row">
          <div className="hero-cta">
            <MagneticButton strength={0.2}>
              <button
                onClick={() => navigate('/register')}
                className="group inline-flex items-center gap-3 rounded-full bg-zupa-purple px-14 py-6 text-xl font-semibold text-white transition-all duration-300 hover:bg-zupa-purple-light hover:shadow-[0_0_40px_rgba(124,58,237,0.5)] active:scale-[0.98]"
                style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
              >
                Start Free Trial
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </MagneticButton>
          </div>
          <div className="hero-cta">
            <MagneticButton strength={0.15}>
              <button
                onClick={scrollToHowItWorks}
                className="inline-flex items-center gap-3 rounded-full border-2 border-white/20 bg-transparent px-14 py-6 text-xl font-semibold text-white/80 transition-all duration-300 hover:border-zupa-purple/40 hover:text-white hover:bg-white/5 active:scale-[0.98]"
                style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
              >
                See How It Works
              </button>
            </MagneticButton>
          </div>
        </div>

        {/* Platform strip */}
        <div className="hero-trust mt-12 flex flex-col items-center gap-4">
          <p
            className="text-[#C8C6D2]/60"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '1.1rem',
              letterSpacing: '0.05em',
            }}
          >
            All major platforms
          </p>
          <div className="flex items-center gap-6">
            {/* LinkedIn */}
            <svg viewBox="0 0 24 24" className="h-7 w-7 text-[#0A66C2] opacity-70 hover:opacity-100 transition-opacity" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            {/* Instagram */}
            <svg viewBox="0 0 24 24" className="h-7 w-7 text-[#E1306C] opacity-70 hover:opacity-100 transition-opacity" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
            </svg>
            {/* X (Twitter) */}
            <svg viewBox="0 0 24 24" className="h-6 w-6 text-[#E8E6F0] opacity-50 hover:opacity-100 transition-opacity" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            {/* Facebook */}
            <svg viewBox="0 0 24 24" className="h-7 w-7 text-[#1877F2] opacity-70 hover:opacity-100 transition-opacity" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}
